import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

// Environment
import { environment } from 'src/environments/environment';

export interface TableState {
    sortField?: string | string[] | null;
    sortOrder?: number | null;
    first?: number;
    rows?: number | null;
    multiSortMeta?: any[];
    selectedFilteringOptions?: string[];
    columnConfig?: { field: string; visible: boolean; width?: number }[];
    [key: string]: any;
}

export interface TableStateResponse {
    table_identifier: string;
    state: TableState;
}

@Injectable({
    providedIn: 'root'
})
export class TableStateService {
    private readonly baseUrl = `/table-states`;

    constructor(private http: HttpClient) { }

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    /**
     * Récupère l'état d'un tableau
     * @param stateId Identifiant unique du tableau
     * @returns Observable de l'état du tableau ou état vide si non trouvé
     */
    public getTableState$(stateId: string): Observable<TableState> {
        return this.http.get<TableStateResponse>(`${this.baseUrl}/${stateId}/`).pipe(
            map(response => response.state || {}),
            catchError(error => {
                if (error.status === 404) {
                    // Si l'état n'existe pas, retourner un état vide
                    return of({});
                }
                // Pour toute autre erreur, retourner un état vide
                console.warn(`Erreur lors de la récupération de l'état du tableau ${stateId}:`, error);
                return of({});
            })
        );
    }

    /**
     * Sauvegarde l'état d'un tableau
     * @param stateId Identifiant unique du tableau
     * @param state État du tableau à sauvegarder
     * @returns Observable de la réponse
     */
    public saveTableState$(stateId: string, state: TableState): Observable<TableStateResponse> {
        const data = { state };
        return this.http.put<TableStateResponse>(`${this.baseUrl}/${stateId}/`, data).pipe(
            catchError(error => {
                console.error(`Erreur lors de la sauvegarde de l'état du tableau ${stateId}:`, error);
                // En cas d'erreur, retourner un observable qui ne fait rien
                return of({ table_identifier: stateId, state });
            })
        );
    }

    /**
     * Sauvegarde l'état d'un tableau de manière silencieuse (sans gestion d'erreur)
     * @param stateId Identifiant unique du tableau
     * @param state État du tableau à sauvegarder
     */
    public saveTableStateSilent(stateId: string, state: TableState): void {
        this.saveTableState$(stateId, state).subscribe({
            next: () => {
                // Sauvegarde réussie, pas d'action nécessaire
            },
            error: () => {
                // Erreur silencieuse, on ne fait rien
            }
        });
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/
}